/**
 * Construye la fecha de inicio de un mes en formato ISO 'YYYY-MM-DD'.
 * Usar siempre esta función (no `new Date().toISOString()`) para evitar
 * desfases por timezone en entornos con offset negativo.
 */
export function fechaInicioMes(mes: number, año: number): string {
  return `${año}-${String(mes).padStart(2, '0')}-01`;
}

/**
 * Construye la fecha de fin de un mes en formato ISO 'YYYY-MM-DD'.
 * Usa `new Date(año, mes, 0).getDate()` para obtener el último día
 * sin depender de timezone (es aritmética de calendario puro).
 */
export function fechaFinMes(mes: number, año: number): string {
  const ultimo = new Date(año, mes, 0).getDate();
  return `${año}-${String(mes).padStart(2, '0')}-${String(ultimo).padStart(2, '0')}`;
}

/**
 * Convierte un `Date` a 'YYYY-MM-DD' usando getters locales (`getFullYear`/`getMonth`/
 * `getDate`) — nunca `.toISOString()`, que expresa el instante en UTC: en timezones con
 * offset negativo (ej. America/Santiago, UTC-3/-4) una fecha tomada de noche ya cayó en
 * el día UTC siguiente, y `.toISOString().slice(0, 10)` devuelve "mañana" en vez de "hoy".
 */
export function fechaISO(fecha: Date): string {
  const año = fecha.getFullYear();
  const mes = String(fecha.getMonth() + 1).padStart(2, '0');
  const dia = String(fecha.getDate()).padStart(2, '0');
  return `${año}-${mes}-${dia}`;
}

/** Fecha de hoy en 'YYYY-MM-DD', segura ante el mismo desfase de timezone que `fechaISO`. */
export function hoyISO(): string {
  return fechaISO(new Date());
}

/**
 * Primera fecha de nacimiento que todavía corresponde a una persona de hasta 120 años.
 * Se resta 121 años y se avanza un día: alguien nacido exactamente 121 años atrás ya
 * supera el límite, pero quien cumple 121 mañana todavía tiene 120 hoy.
 */
export function fechaNacimientoMinima(hoy: Date = new Date()): string {
  const anioLimite = hoy.getFullYear() - 121;
  const mes = hoy.getMonth();
  // El 29 de febrero se recorta al 28 antes de avanzar: el primer nacimiento
  // admisible es el 1 de marzo, no el 2 por normalización de una fecha inexistente.
  const ultimoDia = new Date(anioLimite, mes + 1, 0).getDate();
  const diaLimite = Math.min(hoy.getDate(), ultimoDia);
  return fechaISO(new Date(anioLimite, mes, diaLimite + 1));
}

/** Zona horaria donde opera hoy el sistema. Ver la deuda declarada en `fechaEnChile`. */
export const ZONA_OPERACION = 'America/Santiago';

const FECHA_EN_CHILE = new Intl.DateTimeFormat('en-CA', {
  timeZone: ZONA_OPERACION,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const FORMATO_FECHA = /^\d{4}-\d{2}-\d{2}$/;

/**
 * 'YYYY-MM-DD' de un instante **en Chile**, o `null` si no se puede determinar con certeza.
 *
 * Distinto de `fechaISO`, que usa el reloj de quien mira: esta responde lo mismo desde cualquier
 * parte del mundo. Úsala cuando la respuesta tiene que coincidir con la del servidor — por ejemplo
 * para decidir si un plazo sigue vigente. Este módulo no depende del navegador ni del servidor:
 * `api/_compromisoFoto.ts` importa esta misma función con sufijo `.js` para su runtime ESM.
 *
 * Devuelve `null` en vez de una fecha dudosa porque estas fechas se comparan como texto, y un
 * formato inesperado no da error sino una comparación que resulta verdadera de más.
 * La guarda cubre años de menos de cuatro dígitos y runtimes con datos de idioma recortados
 * donde `en-CA` degrada a `en-US` y produce 'M/D/YYYY'. Ambos deben fallar cerrado.
 *
 * deuda: la zona está fija en Chile. techo: hasta el primer Lumisial fuera del país. disparador:
 * una provincia eclesiástica con `codigo_iso` distinto de 'CL'.
 */
export function fechaEnChile(instante: Date): string | null {
  if (Number.isNaN(instante.getTime())) return null;
  const fecha = FECHA_EN_CHILE.format(instante);
  return FORMATO_FECHA.test(fecha) ? fecha : null;
}

/**
 * Día civil de hoy en Chile, 'YYYY-MM-DD'. Es el valor por defecto de toda fecha que se registra
 * (decisión del usuario, 2026-10-08): después de las 20:00 en Chile `new Date().toISOString()` ya
 * marca el día UTC siguiente y un gasto del 30 caería en el período del mes entrante.
 * Si el runtime no puede resolver la zona (`fechaEnChile` devuelve `null`) cae al reloj local.
 */
export function hoyEnChile(): string {
  return fechaEnChile(new Date()) ?? hoyISO();
}
